import { inject, Injectable } from '@angular/core';

import { SwPush } from '@angular/service-worker';
import { HttpService } from './http.service';
import { environment } from '../../environments/environment';
import { PushType } from '@weavix/models/src/user/User';
import { PushPayload } from '@weavix/models/src/push/push';

const VAPID_SUBSCRIPTION = environment.radioApp ? 'VAPID_SUBSCRIPTION_RADIO' : 'VAPID_SUBSCRIPTION';
const VAPID_PUSH_TYPE = environment.radioApp ? PushType.vapidRadio : PushType.vapid;
const VAPID_PUBLIC_KEY = 'BPzVzuyTaSw4UxPnHLgMpURMAboiJrO5vOzpgBXsO69Wvg5ulfiYkYVp-XglbsDDSa967P-mySRGezad6KlOcNA';
const VAPID_RADIO_PUBLIC_KEY = 'BDVu2RcchjFYk9ZqAWL8aXo4zEKTiethfjBR1XW80dQmPrANM0g07by72JlcFAV6MHGWUDZlJEPzoRVl_uySqZg';

@Injectable({
    providedIn: 'root',
})
export class PushService {
    private readonly swPush = inject(SwPush);
    private readonly httpService = inject(HttpService);

    /** Registers the given user for desktop push notifications. */
    async register(userId: string) {
        try {
            await this.addPush(userId);
        } catch (e) {
            const subId = localStorage.getItem(VAPID_SUBSCRIPTION);
            if (subId) {
                // Stored subscription might be old public key or type. Retry.
                try {
                    await this.deregister(userId);
                    await this.addPush(userId);
                    return;
                } catch (e) {
                    console.error(e);
                }
            }
            if (e instanceof DOMException && e.name === 'NotAllowedError') {
                console.warn('Denied permission for web push notifications.', e);
            } else {
                console.error('Failed to register push notifications.', e);
            }
        }
    }

    private async addPush(userId: string) {
        const data = await this.swPush.requestSubscription({
            serverPublicKey: environment.radioApp ? VAPID_RADIO_PUBLIC_KEY : VAPID_PUBLIC_KEY,
        });
        await this.httpService.put(null, `/core/users/${userId}/add-push`, { id: data, type: VAPID_PUSH_TYPE });
        localStorage.setItem(VAPID_SUBSCRIPTION, JSON.stringify(data));
    }

    /** Unregisters the user. */
    async deregister(userId: string) {
        const vapid = localStorage.getItem(VAPID_SUBSCRIPTION);
        if (!vapid) return;

        localStorage.removeItem(VAPID_SUBSCRIPTION);
        try {
            await this.swPush.unsubscribe();
        } catch (e) {
            console.error('Failed to unsubscribe from service worker push notifications.', e);
        }
        const data = JSON.parse(vapid);
        try {
            await this.httpService.put(null, `/core/users/${userId}/remove-push`, { id: data, type: VAPID_PUSH_TYPE });
        } catch (e) {
            console.error('Failed to deregister push notifications.', e);
        }
    }

    /** Clears all notifications matching a given filter. */
    async clearNotifications(filter: (payload: PushPayload | null) => boolean): Promise<void> {
        if (!this.swPush.isEnabled) return;

        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (!registration) return;

            const notifications = await registration.getNotifications();
            notifications
                .filter(notification => typeof notification.data === 'object' && filter(notification.data))
                .forEach(notification => notification.close());
        } catch (e) {
            console.error(`Failed to clear some push notifications.`, e);
        }
    }
}
